import type {
  AcpLifecycleEvent,
  AgentPanelHandle,
  ChatSettingsData,
  SessionStateMessage,
  TokenUsage,
} from '@simple-agent-manager/acp-client';
import { AgentPanel,useAcpMessages, useAcpSession } from '@simple-agent-manager/acp-client';
import type { AgentInfo } from '@simple-agent-manager/shared';
import { AGENT_PERMISSION_MODE_LABELS,VALID_PERMISSION_MODES } from '@simple-agent-manager/shared';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getAgentSettings,
  getTerminalToken,
  getTranscribeApiUrl,
  saveAgentSettings,
} from '../lib/api';
import { reportError } from '../lib/error-reporter';

interface ChatSessionProps {
  /** Workspace ID for token fetching */
  workspaceId: string;
  /** Workspace URL (e.g., https://ws-xxx.domain) */
  workspaceUrl: string;
  /** Agent session ID */
  sessionId: string;
  /** Preferred agent type for this session */
  preferredAgentId?: string;
  /** Bound worktree path for this chat session */
  worktreePath?: string | null;
  /** All configured agents */
  configuredAgents: AgentInfo[];
  /** Whether this tab is currently visible */
  active: boolean;
  /** Called on any activity (for idle detection) */
  onActivity?: () => void;
  /** Called when token usage changes (for sidebar aggregation) */
  onUsageChange?: (sessionId: string, usage: TokenUsage) => void;
}

/** Imperative handle for ChatSession — mirrors AgentPanelHandle. */
export type ChatSessionHandle = AgentPanelHandle;

/**
 * Self-contained chat session component.
 * Each instance owns its own ACP WebSocket connection, message history,
 * and agent selection — fully independent of other chat tabs.
 */
export const ChatSession = React.forwardRef<ChatSessionHandle, ChatSessionProps>(
  function ChatSession(
    {
      workspaceId,
      workspaceUrl,
      sessionId,
      preferredAgentId,
      worktreePath,
      active,
      onActivity,
      onUsageChange,
    },
    ref
  ) {
    const agentPanelRef = useRef<AgentPanelHandle>(null);

    useImperativeHandle(ref, () => ({
      focusInput: () => agentPanelRef.current?.focusInput(),
    }));
    const wsUrlCacheRef = useRef<{ url: string; resolvedAt: number } | null>(null);

    // Resolve transcription API URL once (stable across renders)
    const transcribeApiUrl = useMemo(() => getTranscribeApiUrl(), []);

    // Parse workspace URL once
    const wsHostInfo = useMemo(() => {
      if (!workspaceUrl) return null;
      try {
        const url = new URL(workspaceUrl);
        const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsProtocol}//${url.host}`;
      } catch {
        return null;
      }
    }, [workspaceUrl]);

    // Lifecycle event callback — routes ACP lifecycle events to the error reporter
    // for CF Workers observability. Enriches with workspaceId/sessionId context.
    // Also invalidates the WS URL cache on error/reconnecting so fresh tokens are used.
    const handleLifecycleEvent = useCallback(
      (event: AcpLifecycleEvent) => {
        // Invalidate cached URL on error or reconnecting so reconnect fetches a fresh token
        if (event.context?.['state'] === 'error' || event.context?.['state'] === 'reconnecting' ||
            event.message.includes('WebSocket closed') || event.message.includes('WebSocket error')) {
          wsUrlCacheRef.current = null;
        }
        reportError({
          level: event.level,
          message: event.message,
          source: event.source,
          context: {
            ...event.context,
            workspaceId,
            sessionId,
          },
        });
      },
      [workspaceId, sessionId]
    );

    useEffect(() => {
      wsUrlCacheRef.current = null;
    }, [wsHostInfo, workspaceId, sessionId, worktreePath]);

    const resolveWsUrl = useCallback(async (): Promise<string | null> => {
      if (!wsHostInfo) return null;

      const cached = wsUrlCacheRef.current;
      if (cached && Date.now() - cached.resolvedAt < 15_000) {
        return cached.url;
      }

      reportError({
        level: 'info',
        message: 'Resolving ACP WebSocket URL with fresh terminal token',
        source: 'acp-chat',
        context: { workspaceId, sessionId },
      });

      try {
        const { token } = await getTerminalToken(workspaceId);
        const sessionQuery = `&sessionId=${encodeURIComponent(sessionId)}`;
        const worktreeQuery = worktreePath ? `&worktree=${encodeURIComponent(worktreePath)}` : '';
        const url = `${wsHostInfo}/agent/ws?token=${encodeURIComponent(token)}${sessionQuery}${worktreeQuery}`;
        wsUrlCacheRef.current = { url, resolvedAt: Date.now() };
        return url;
      } catch (err) {
        reportError({
          level: 'error',
          message: `Terminal token fetch failed: ${err instanceof Error ? err.message : String(err)}`,
          source: 'acp-chat',
          context: { workspaceId, sessionId },
        });
        throw err;
      }
    }, [wsHostInfo, workspaceId, sessionId, worktreePath]);

    // Each chat session gets its own message store.
    // No client-side persistence — on reconnect, LoadSession replays the full
    // conversation from the agent via session/update notifications.
    const acpMessages = useAcpMessages();

    // Handle agent auto-selection on first connection using event-driven approach.
    // This replaces the useEffect-based logic and prevents infinite loops by design.
    const hasAutoSelectedRef = useRef(false);

    // NOTE: switchAgent is used in handleFirstConnect but comes from acpSession below.
    // We store it in a ref to break the circular dependency.
    const switchAgentRef = useRef<((agentId: string) => void) | null>(null);

    const handleFirstConnect = useCallback((sessionState: SessionStateMessage) => {
      // Only auto-select if we haven't already done so for this session
      if (hasAutoSelectedRef.current) return;

      // Don't auto-select if:
      // 1. No preferred agent is specified
      // 2. An agent is already running (agentType matches preferredAgentId)
      // 3. The session is in an error state
      if (!preferredAgentId) return;
      if (sessionState.agentType === preferredAgentId) return;
      if (sessionState.status === 'error') return;

      // Only auto-select for idle sessions (no agent running yet)
      if (sessionState.status !== 'idle') return;

      hasAutoSelectedRef.current = true;
      reportError({
        level: 'info',
        message: `Auto-selecting agent on first connect: ${preferredAgentId}`,
        source: 'acp-chat',
        context: {
          workspaceId,
          sessionId,
          preferredAgentId,
          currentAgentType: sessionState.agentType,
          sessionStatus: sessionState.status
        },
      });
      switchAgentRef.current?.(preferredAgentId);
    }, [preferredAgentId, workspaceId, sessionId]);

    // Own ACP session hook — separate WebSocket per chat tab
    const acpSession = useAcpSession({
      wsUrl: null,
      resolveWsUrl,
      onAcpMessage: acpMessages.processMessage,
      onLifecycleEvent: handleLifecycleEvent,
      onPrepareForReplay: acpMessages.prepareForReplay,
      onFirstConnect: handleFirstConnect,
    });

    const { agentType, state, switchAgent } = acpSession;
    switchAgentRef.current = switchAgent;
    const { clear: clearMessages } = acpMessages;

    // Clear messages when no agent session exists (idle SessionHost).
    // Replay clearing is now handled synchronously by onPrepareForReplay
    // (called from useAcpSession when session_state arrives with replayCount > 0)
    // which avoids the race where this useEffect fires after replay messages
    // have already been appended.
    useEffect(() => {
      if (state === 'no_session') {
        reportError({
          level: 'info',
          message: 'Clearing messages on no_session',
          source: 'acp-chat',
          context: { workspaceId, sessionId },
        });
        clearMessages();
      }
    }, [state, clearMessages, workspaceId, sessionId]);

    // Report activity
    const handleActivity = useCallback(() => {
      onActivity?.();
    }, [onActivity]);

    useEffect(() => {
      if (acpMessages.items.length > 0) {
        handleActivity();
      }
    }, [acpMessages.items.length, handleActivity]);

    // Report token usage changes to parent for sidebar aggregation
    useEffect(() => {
      if (acpMessages.usage.totalTokens > 0) {
        onUsageChange?.(sessionId, acpMessages.usage);
      }
    }, [acpMessages.usage, sessionId, onUsageChange]);

    // ── Agent settings ──
    const [agentSettings, setAgentSettings] = useState<ChatSettingsData | null>(null);
    const [agentSettingsLoading, setAgentSettingsLoading] = useState(false);

    // Build permission mode options from shared constants
    const permissionModes = useMemo(
      () =>
        VALID_PERMISSION_MODES.map((mode) => ({
          value: mode,
          label: AGENT_PERMISSION_MODE_LABELS[mode] ?? mode,
        })),
      []
    );

    // Fetch settings when agent type is known
    const activeAgentType = agentType ?? preferredAgentId;
    useEffect(() => {
      if (!activeAgentType) return;
      let cancelled = false;
      setAgentSettingsLoading(true);

      getAgentSettings(activeAgentType)
        .then((result) => {
          if (cancelled) return;
          setAgentSettings({
            model: result.model,
            permissionMode: result.permissionMode,
          });
        })
        .catch(() => {
          if (cancelled) return;
          // No saved settings — use defaults
          setAgentSettings({ model: null, permissionMode: null });
        })
        .finally(() => {
          if (!cancelled) setAgentSettingsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, [activeAgentType]);

    const handleSaveSettings = useCallback(
      async (data: { model?: string | null; permissionMode?: string | null }) => {
        if (!activeAgentType) return;
        const result = await saveAgentSettings(activeAgentType, {
          model: data.model,
          permissionMode: data.permissionMode as
            | import('@simple-agent-manager/shared').AgentPermissionMode
            | null
            | undefined,
        });
        setAgentSettings({
          model: result.model,
          permissionMode: result.permissionMode,
        });
      },
      [activeAgentType]
    );

    const handleError = useCallback(
      (info: { message: string; source: string; context?: Record<string, unknown> }) => {
        reportError({
          level: 'error',
          message: info.message,
          source: info.source,
          context: info.context,
        });
      },
      []
    );

    return (
      <div
        className="h-full overflow-hidden flex-col"
        style={{ display: active ? 'flex' : 'none' }}
      >
        <AgentPanel
          ref={agentPanelRef}
          session={acpSession}
          messages={acpMessages}
          availableCommands={acpMessages.availableCommands}
          transcribeApiUrl={transcribeApiUrl}
          agentSettings={agentSettings}
          agentSettingsLoading={agentSettingsLoading}
          permissionModes={permissionModes}
          onSaveSettings={handleSaveSettings}
          onError={handleError}
        />
      </div>
    );
  }
);
